import { SocialLogin } from '@capgo/capacitor-social-login';
import { BiometricAuth, BiometryType } from '@aparajita/capacitor-biometric-auth';
import { Capacitor } from '@capacitor/core';
import { Preferences } from '@capacitor/preferences';
import { capacitorDbService } from './capacitorDb.service';

// ============================================================
// INITIALIZE (call once in App.tsx on app start)
// ============================================================
export const initializeSocialLogin = async () => {
  try {
    console.log('=== SOCIAL LOGIN INIT ===');
    console.log('Web Client ID:', '206594127331-mpf3fi6em5b0r1jujvuo09htu5ver1q5.apps.googleusercontent.com');
    console.log('iOS Client ID:', '206594127331-lld1elj6dmbjp13u2ksudel0a6btqu7o.apps.googleusercontent.com');
    console.log('Platform:', Capacitor.getPlatform());

    // Pass options directly - required by CapGo plugin
    await SocialLogin.initialize({
      google: {
        webClientId: '206594127331-mpf3fi6em5b0r1jujvuo09htu5ver1q5.apps.googleusercontent.com',
        iOSClientId: '206594127331-lld1elj6dmbjp13u2ksudel0a6btqu7o.apps.googleusercontent.com',
        mode: 'online',
      },
      apple: {
        clientId: 'com.nurwallet.app',
        redirectUrl: 'https://nurwallet.app/callback',
      }
    });
    console.log('[SocialAuth] SocialLogin initialized');
    return { success: true };
  } catch (error) {
    console.error('[SocialAuth] Initialize error:', error);
    return { success: false, error: 'Failed to initialize social login' };
  }
};

// ============================================================
// GOOGLE SIGN-IN (OFFLINE — stores in SQLite directly)
// ============================================================
export const googleSignIn = async () => {
  try {
    console.log('=== GOOGLE LOGIN START ===');

    const result = await SocialLogin.login({
      provider: 'google',
      options: {
        scopes: ['email', 'profile']
      }
    });

    console.log('=== GOOGLE LOGIN RESULT ===');
    console.log(JSON.stringify(result, null, 2));

    const googleResult = result.result;
    if (!googleResult || googleResult.responseType !== 'online' || !googleResult.idToken) {
      throw new Error('Invalid Google response');
    }

    const profile = googleResult.profile;
    if (!profile?.email) throw new Error('No email from Google');

    await capacitorDbService.init();

    let user = await capacitorDbService.getUserByEmail(profile.email.toLowerCase());

    if (!user) {
      user = await capacitorDbService.createUser({
        email: profile.email.toLowerCase(),
        password: `google_${profile.id || Date.now()}`,
        fullName: profile.name || 'Google User',
        familyStatus: 'SINGLE',
      });
    }

    await Preferences.set({ key: 'auth_method', value: 'google' });

    // Return FULL user object with all required fields for authStore
    return {
      success: true,
      user: {
        id: user.id,
        email: user.email,
        password: user.password,
        fullName: user.fullName,
        familyStatus: user.familyStatus || 'SINGLE',
        tier: user.tier || 'free',
        healthScore: user.healthScore || 0,
        healthScoreZone: user.healthScoreZone || 'CRITICAL',
        createdAt: user.createdAt || new Date().toISOString(),
        updatedAt: user.updatedAt || new Date().toISOString(),
      },
    };
  } catch (error) {
    console.error('=== FULL GOOGLE ERROR ===');
    console.error(error);

    if (error instanceof Error) {
      console.error(error.message);
      console.error(error.stack);
    }
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Google login failed',
    };
  }
};

// ============================================================
// APPLE SIGN-IN (OFFLINE — stores in SQLite directly)
// ============================================================
export const appleSignIn = async () => {
  try {
    const result = await SocialLogin.login({
      provider: 'apple',
      options: { scopes: ['email', 'name'] }
    });

    const appleResult = result.result;
    if (!appleResult || !appleResult.idToken) {
      throw new Error('Invalid Apple response');
    }

    const profile = appleResult.profile;
    const email = profile?.email;

    if (!email) throw new Error('No email from Apple');

    await capacitorDbService.init();

    let user = await capacitorDbService.getUserByEmail(email.toLowerCase());

    if (!user) {
      const fullName = profile?.givenName && profile?.familyName
        ? `${profile.givenName} ${profile.familyName}`
        : 'Apple User';

      user = await capacitorDbService.createUser({
        email: email.toLowerCase(),
        password: `apple_${profile?.user || Date.now()}`,
        fullName,
        familyStatus: 'SINGLE',
      });
    }

    await Preferences.set({ key: 'auth_method', value: 'apple' });

    // Return FULL user object with all required fields for authStore
    return {
      success: true,
      user: {
        id: user.id,
        email: user.email,
        password: user.password,
        fullName: user.fullName,
        familyStatus: user.familyStatus || 'SINGLE',
        tier: user.tier || 'free',
        healthScore: user.healthScore || 0,
        healthScoreZone: user.healthScoreZone || 'CRITICAL',
        createdAt: user.createdAt || new Date().toISOString(),
        updatedAt: user.updatedAt || new Date().toISOString(),
      },
    };
  } catch (error) {
    console.error('Apple Sign-In error:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Apple login failed',
    };
  }
};

// ============================================================
// BIOMETRIC AUTH (Face ID / Fingerprint)
// ============================================================
export const biometricAuth = {
  async isAvailable(): Promise<boolean> {
    try {
      const result = await BiometricAuth.checkBiometry();
      return result.biometryType !== BiometryType.none;
    } catch {
      return false;
    }
  },

  async getBiometryType(): Promise<string> {
    try {
      const result = await BiometricAuth.checkBiometry();
      return String(result.biometryType);
    } catch {
      return 'none';
    }
  },

  async enableBiometricLogin(email: string, password: string) {
    try {
      await Preferences.set({ key: 'biometric_email', value: email });
      await Preferences.set({ key: 'biometric_password', value: password });
      await Preferences.set({ key: 'biometric_enabled', value: 'true' });
      return { success: true };
    } catch {
      return { success: false, error: 'Failed to enable biometric login' };
    }
  },

  async disableBiometricLogin() {
    await Preferences.remove({ key: 'biometric_email' });
    await Preferences.remove({ key: 'biometric_password' });
    await Preferences.remove({ key: 'biometric_enabled' });
    return { success: true };
  },

  async isBiometricEnabled(): Promise<boolean> {
    const { value } = await Preferences.get({ key: 'biometric_enabled' });
    return value === 'true';
  },

  async authenticateWithBiometric(): Promise<{ success: boolean; email?: string; password?: string; error?: string }> {
    try {
      await BiometricAuth.authenticate({
        reason: 'Log in to NurWallet',
        cancelTitle: 'Cancel',
        allowDeviceCredential: true,
      });

      const [emailResult, passwordResult] = await Promise.all([
        Preferences.get({ key: 'biometric_email' }),
        Preferences.get({ key: 'biometric_password' }),
      ]);

      if (!emailResult.value || !passwordResult.value) {
        return { success: false, error: 'No stored credentials found' };
      }

      return { success: true, email: emailResult.value, password: passwordResult.value };
    } catch {
      return { success: false, error: 'Biometric authentication failed' };
    }
  },
};

// ============================================================
// LOGOUT
// ============================================================
export const socialLogout = async () => {
  try {
    // Clear ALL possible session keys for consistency
    await Preferences.remove({ key: 'auth_token' });
    await Preferences.remove({ key: 'user_id' });
    await Preferences.remove({ key: 'auth_method' });
    await Preferences.remove({ key: 'nurwallet_session' });
    await Preferences.remove({ key: 'accessToken' });
    await Preferences.remove({ key: 'current_user_id' });
    await Preferences.remove({ key: 'user' });
    await Preferences.remove({ key: 'user_tier' });

    try { await SocialLogin.logout({ provider: 'google' }); } catch (e) {}
    try { await SocialLogin.logout({ provider: 'apple' }); } catch (e) {}

    (window as any).__NURWALLET_USER_ID__ = null;
    return { success: true };
  } catch {
    return { success: false, error: 'Logout failed' };
  }
};